import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {usePageText} from '../context/page-text-context';
import {useRepair} from '../context/repair-context';
import {getDefaultHeaders} from '../utils/api-headers';
import {schedulingLoaded, schedulingLoading} from '../features/scheduling/schedulerSlice';
import {Loading} from './loading';
import Modal from './modal';
import {PlainTextArea} from './tracking-text-input';
import {CancelButtonFilled, ConfirmButton} from './buttons';
import {WarningSVG} from './svgs';

function CancelModal({modalOpen, setModalOpen}) {
  const baseUrl = import.meta.env.VITE_REACT_APP_API_HOST;
  const dispatch = useDispatch();
  const pageText = usePageText();
  const {repair, refetch} = useRepair();
  const {
    tracking: {cancelModal, fixtSupportEmail},
    closeButton,
  } = pageText;
  const {loading: schedulerLoading} = useSelector((state) => state.scheduler);
  const [schedulingError, setSchedulingError] = useState(null);
  const [cancelComment, setCancelComment] = useState('');

  const handleCancel = async () => {
    dispatch(schedulingLoading());
    setSchedulingError(null);
    const cancelRequestUrl = `${baseUrl}/web_repairs/${repair.id}/cancel`;
    const cancelCommentUrl = `${baseUrl}/web_repairs/cancel/comment/${repair.id}`;
    const headers = getDefaultHeaders(repair.id);
    const cancelRequestOptions = {
      headers,
      method: 'PATCH',
    };
    const cancelCommentOptions = {
      headers,
      method: 'POST',
      body: JSON.stringify({comment: cancelComment}),
    };

    try {
      await fetch(cancelRequestUrl, cancelRequestOptions);
      await fetch(cancelCommentUrl, cancelCommentOptions);
      dispatch(schedulingLoaded());
      refetch();
    } catch (error) {
      dispatch(schedulingLoaded());
      setSchedulingError(error.message);
      setModalOpen(false);
      console.error(error);
    }
  };

  if (schedulingError) {
    return (
      <div>
        <div className="text-red">
          <p data-testid="cancel-error">
            {cancelModal.errorMessage}
            <a href={`mailto:${fixtSupportEmail}`}>{fixtSupportEmail}</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cancel-modal">
      <Modal handleModalClose={() => setModalOpen(false)} visible={modalOpen}>
        <div className="flex items-center flex-col justify-between text-center">
          <div className="flex flex-col gap-4 p-4">
            <span className="rounded-full h-[60px] w-[60px] flex justify-center items-center bg-light-gray self-center">
              <WarningSVG />
            </span>
            <h2 className="font-bold text-black text-xl">{cancelModal.modalHeader}</h2>

            {schedulerLoading ? (
              <div className="self-center">
                <Loading />
              </div>
            ) : (
              <PlainTextArea
                className="border rounded-lg box-border text-black h-36 w-full p-2 resize-none"
                name="cancel_comment"
                onChange={setCancelComment}
                placeholder={cancelModal.modalPlaceholder}
                value={cancelComment}
              />
            )}

            <div className="flex flex-col gap-2" data-testid="cancel-btns">
              <CancelButtonFilled disabled={schedulerLoading} onClick={handleCancel}>
                {cancelModal.confirmCancel}
              </CancelButtonFilled>
              <ConfirmButton disabled={schedulerLoading} onClick={() => setModalOpen(false)}>
                {closeButton}
              </ConfirmButton>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CancelModal;
